// src/components/messaging/ChatPanel.tsx
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import { Conversation, Group, Message, GroupMessage, MessagingUser } from '../../types/messaging';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import TypingIndicator from './TypingIndicator';

interface ChatPanelProps {
  conversation?: Conversation | null;
  group?: Group | null;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ conversation, group }) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const {
    state,
    fetchMessages,
    fetchGroupMessages,
    sendMessage,
    sendMediaMessage,
    sendGroupMessage,
    sendGroupMediaMessage,
    editMessage,
    deleteMessage,
    editGroupMessage,
    deleteGroupMessage,
    deleteConversation,
    leaveGroup,
    sendTyping,
    addReaction,
    removeReaction,
    addGroupReaction,
    removeGroupReaction,
  } = useMessaging();

  const { messages, groupMessages, typingUsers, onlineUsers, isLoadingMessages } = state;

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [replyTo, setReplyTo] = useState<Message | GroupMessage | null>(null);
  const [editingMessage, setEditingMessage] = useState<Message | GroupMessage | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<Message | GroupMessage | null>(null);

  const isGroup = !!group;
  const currentUserId = authState.user?.id || 0;

  // Get current messages
  const currentMessages = useMemo(() => {
    if (isGroup && group) {
      return groupMessages[group.id] || [];
    }
    if (conversation) {
      return messages[conversation.id] || [];
    }
    return [];
  }, [isGroup, group, conversation, messages, groupMessages]);

  // Get typing users for this chat
  const typingUserIds = useMemo(() => {
    const key = isGroup ? `group_${group?.id}` : `conv_${conversation?.id}`;
    return typingUsers[key] || [];
  }, [isGroup, group?.id, conversation?.id, typingUsers]);

  // Get typing user names
  const typingUserNames = useMemo(() => {
    if (typingUserIds.length === 0) return [];

    if (isGroup && group?.members) {
      return typingUserIds
        .filter((id) => id !== currentUserId)
        .map((id) => {
          const member = group.members?.find((m) => m.user_id === id);
          return member?.user?.name || 'Unknown';
        });
    }

    if (!isGroup && conversation) {
      return typingUserIds
        .filter((id) => id !== currentUserId)
        .map(() => conversation.other_user?.name || 'Unknown');
    }

    return [];
  }, [typingUserIds, isGroup, group, conversation, currentUserId]);

  // Get sender info for messages
  const getSender = useCallback(
    (senderId: number): MessagingUser | undefined => {
      if (isGroup && group?.members) {
        const member = group.members.find((m) => m.user_id === senderId);
        return member?.user;
      }
      if (!isGroup && conversation) {
        if (senderId === conversation.other_user?.id) {
          return conversation.other_user;
        }
      }
      return undefined;
    },
    [isGroup, group, conversation]
  );

  // Header info
  const name = isGroup ? group?.name : conversation?.other_user?.name;
  const avatarUrl = isGroup ? group?.image_url : conversation?.other_user?.avatar_url;
  const isOnline = !isGroup && conversation?.other_user?.id
    ? onlineUsers.has(conversation.other_user.id)
    : false;
  const memberCount = isGroup ? group?.member_count || group?.members?.length : undefined;

  // Get initials
  const getInitials = (name?: string): string => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Handle load more
  const handleLoadMore = useCallback(async (): Promise<boolean> => {
    if (currentMessages.length === 0) return false;

    const oldestMessage = currentMessages[0];
    let result = false;

    if (isGroup && group) {
      result = await fetchGroupMessages(group.id, oldestMessage.id);
    } else if (conversation) {
      result = await fetchMessages(conversation.id, oldestMessage.id);
    }

    setHasMore(result);
    return result;
  }, [currentMessages, isGroup, group, conversation, fetchGroupMessages, fetchMessages]);

  // Handle send message
  const handleSend = useCallback(
    async (content: string, replyToId?: number) => {
      if (isGroup && group) {
        await sendGroupMessage(group.id, content, replyToId);
      } else if (conversation) {
        await sendMessage(conversation.id, content, replyToId);
      }
    },
    [isGroup, group, conversation, sendGroupMessage, sendMessage]
  );

  // Handle send file (including audio)
  const handleSendFile = useCallback(
    async (file: File, type: 'image' | 'file' | 'audio', replyToId?: number) => {
      if (isGroup && group) {
        await sendGroupMediaMessage(group.id, file, type, replyToId);
      } else if (conversation) {
        await sendMediaMessage(conversation.id, file, type, replyToId);
      }
    },
    [isGroup, group, conversation, sendGroupMediaMessage, sendMediaMessage]
  );

  // Handle typing
  const handleTyping = useCallback(
    (isTyping: boolean) => {
      if (isGroup && group) {
        sendTyping(undefined, group.id, isTyping);
      } else if (conversation) {
        sendTyping(conversation.id, undefined, isTyping);
      }
    },
    [isGroup, group, conversation, sendTyping]
  );

  // Handle edit submit
  const handleEditSubmit = useCallback(
    async (messageId: number, content: string) => {
      if (isGroup && group) {
        await editGroupMessage(group.id, messageId, content);
      } else if (conversation) {
        await editMessage(conversation.id, messageId, content);
      }
    },
    [isGroup, group, conversation, editMessage, editGroupMessage]
  );

  // Handle delete - show confirmation dialog
  const handleDeleteRequest = useCallback((message: Message | GroupMessage) => {
    setMessageToDelete(message);
    setDeleteDialogOpen(true);
  }, []);

  // Confirm delete
  const handleDeleteConfirm = useCallback(async () => {
    if (!messageToDelete) return;

    if (isGroup && group) {
      await deleteGroupMessage(group.id, messageToDelete.id);
    } else if (conversation) {
      await deleteMessage(conversation.id, messageToDelete.id);
    }

    setDeleteDialogOpen(false);
    setMessageToDelete(null);
  }, [isGroup, group, conversation, deleteMessage, deleteGroupMessage, messageToDelete]);

  // Cancel delete
  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setMessageToDelete(null);
  }, []);

  // Handle reaction
  const handleReaction = useCallback(
    async (message: Message | GroupMessage, reaction: string) => {
      if (isGroup && group) {
        await addGroupReaction(group.id, message.id, reaction);
      } else if (conversation) {
        await addReaction(conversation.id, message.id, reaction);
      }
    },
    [isGroup, group, conversation, addReaction, addGroupReaction]
  );

  // Handle remove reaction
  const handleRemoveReaction = useCallback(
    async (message: Message | GroupMessage) => {
      if (isGroup && group) {
        await removeGroupReaction(group.id, message.id);
      } else if (conversation) {
        await removeReaction(conversation.id, message.id);
      }
    },
    [isGroup, group, conversation, removeReaction, removeGroupReaction]
  );

  // Handle menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleDeleteConversation = async () => {
    handleMenuClose();
    if (conversation) {
      await deleteConversation(conversation.id);
    }
  };

  const handleLeaveGroup = async () => {
    handleMenuClose();
    if (group) {
      await leaveGroup(group.id);
    }
  };

  if (!conversation && !group) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'background.paper',
        }}
      >
        {/* Avatar */}
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          variant="dot"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: isOnline ? '#10b981' : 'transparent',
              boxShadow: isOnline ? '0 0 0 2px white' : 'none',
              width: 12,
              height: 12,
              borderRadius: '50%',
            },
          }}
        >
          <Avatar
            src={avatarUrl || undefined}
            sx={{
              width: 44,
              height: 44,
              backgroundColor: isGroup ? 'secondary.main' : 'primary.main',
            }}
          >
            {getInitials(name)}
          </Avatar>
        </Badge>

        {/* Info */}
        <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isGroup ? (
              <>
                <GroupIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                {t('messaging.members', '{{count}} membres', { count: memberCount || 0 })}
              </>
            ) : isOnline ? (
              <Box component="span" sx={{ color: 'success.main' }}>
                {t('messaging.online', 'En ligne')}
              </Box>
            ) : (
              t('messaging.offline', 'Hors ligne')
            )}
          </Typography>
        </Box>

        {/* Menu button */}
        <IconButton onClick={handleMenuOpen} size="small">
          <MoreVertIcon />
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {isGroup ? (
            [
              <MenuItem key="info" onClick={handleMenuClose}>
                <ListItemIcon>
                  <InfoIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('messaging.groupInfo', 'Infos du groupe')}</ListItemText>
              </MenuItem>,
              <Divider key="divider" />,
              <MenuItem key="leave" onClick={handleLeaveGroup} sx={{ color: 'error.main' }}>
                <ListItemIcon>
                  <ExitToAppIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>{t('messaging.leaveGroup', 'Quitter le groupe')}</ListItemText>
              </MenuItem>,
            ]
          ) : (
            <MenuItem onClick={handleDeleteConversation} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>{t('messaging.deleteConversation', 'Supprimer la conversation')}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Messages */}
      <MessageList
        messages={currentMessages}
        currentUserId={currentUserId}
        isGroup={isGroup}
        isLoading={isLoadingMessages}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        onReply={setReplyTo}
        onEdit={setEditingMessage}
        onDelete={handleDeleteRequest}
        onReaction={handleReaction}
        onRemoveReaction={handleRemoveReaction}
        getSender={getSender}
      />

      {/* Typing indicator */}
      <TypingIndicator userNames={typingUserNames} />

      {/* Input */}
      <MessageInput
        onSend={handleSend}
        onSendFile={handleSendFile}
        onTyping={handleTyping}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        editMessage={editingMessage}
        onCancelEdit={() => setEditingMessage(null)}
        onEditSubmit={handleEditSubmit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('messaging.confirmDelete', 'Supprimer le message')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t(
              'messaging.confirmDeleteMessage',
              'Êtes-vous sûr de vouloir supprimer ce message ? Cette action est irréversible.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.delete', 'Supprimer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatPanel;
